import { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import SmartTextInput from "@/components/SmartTextInput";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email && !form.phone) {
      toast.error("Please provide either an email or a phone number so we can reply.");
      return;
    }
    setBusy(true);
    try {
      await http.post("/messages", form);
      setSent(true);
      toast.success("Message sent — we'll get back to you soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="contact-thanks" className="max-w-[560px] mx-auto px-6 py-24 text-center">
        <div className="overline mb-3">MESSAGE RECEIVED</div>
        <h1 className="font-display text-4xl">Thank you.</h1>
        <p className="mt-4 text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
          Your message is now in our inbox. A member of the Social Pulse team will reach out shortly.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="max-w-[560px] mx-auto px-6 py-24">
      <div className="overline mb-3">CONTACT US</div>
      <h1 className="font-display text-4xl">We'd love to hear from you.</h1>
      <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
        Share feedback, report a story, ask for help, or partner with us.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-4">
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider">Your name</span>
          <input data-testid="contact-name" required value={form.name} onChange={set("name")} className="w-full mt-2 rounded-lg border px-4 py-3 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider">Email</span>
            <input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className="w-full mt-2 rounded-lg border px-4 py-3 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider">Phone (optional)</span>
            <input data-testid="contact-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="+1…" className="w-full mt-2 rounded-lg border px-4 py-3 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
        </div>
        <p className="text-[11px]" style={{ color: "var(--wofy-text-soft)" }}>Provide at least one contact method.</p>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider">Subject</span>
          <input data-testid="contact-subject" required value={form.subject} onChange={set("subject")} className="w-full mt-2 rounded-lg border px-4 py-3 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider">Message</span>
          <div className="mt-2">
            <SmartTextInput
              value={form.body}
              onChange={(v) => setForm({ ...form, body: v })}
              placeholder="What's on your mind?"
              rows={6}
              maxLength={5000}
              testid="contact-body"
            />
          </div>
        </label>
        <button data-testid="contact-submit" disabled={busy} className="wofy-btn-primary w-full justify-center disabled:opacity-60">
          {busy ? "Sending…" : "Send message"}
        </button>
      </form>
    </div>
  );
}
